import React, { useMemo } from 'react';
import { DemeterFeatureType, DemeterPermissionType, DemeterUserType } from '../../../Generated/Raven-Demeter';
import { useApplicationSelector } from '../../../Redux/ReduxStore';
import { selectUserType } from '../../../Redux/Slices/UserSlice';
import PageHeader from '../../Components/Headers/PageHeader';
import Tabs from '../../Components/Navigation/Tabs/Tabs';
import useFeatureFlag from '../../Services/FeatureFlags/useFeatureFlagHook';
import useLanguage from '../../Services/Language/useLanguageHook';
import usePermission from '../../Services/Permissions/usePermissionHook';
import styles from './AdministrationPage.module.scss';
import AnalyticsPage from './Analytics/AnalyticsPage';
import CompaniesPage from './Companies/CompaniesPage';
import CompanyUsersPage from './Companies/CompanyUsersPage';
import MaintenancePage from './Maintenance/MaintenancePage';
import MarketIndicatorsMangementPage from './MarketIndicatorsManagement/MarketIndicatorsManagementPage';
import MurexInstrumentMappingPage from './MurexInstrumentMapping/MurexInstrumentMappingPage';
import PositionsPage from './Positions/PositionsPage';
import UsersPage from './Users/UsersPage';
import WorkerLogsPage from './WorkerLogs/WorkerLogsPage';

const AdministrationPage: React.FC = () => {
    const [translations] = useLanguage();
    const pageTitle = translations.administration.title;
    const currentUserType = useApplicationSelector(selectUserType);
    const isPremiumUser = useMemo(() => currentUserType === DemeterUserType.Premium, [currentUserType]);
    const isAdministrator = useMemo(() => currentUserType === DemeterUserType.Administrator, [currentUserType]);

    const tabOptions = [
        {
            title: translations.users.title.list,
            key: 'Users',
            component: <UsersPage />,
            showTab: useFeatureFlag(DemeterFeatureType.Users),
            testId: 'UsersTab',
        },
        {
            title: translations.companies.title.list,
            key: 'Companies',
            component: <CompaniesPage />,
            showTab: useFeatureFlag(DemeterFeatureType.Companies),
            testId: 'CompaniesTab',
        },
        {
            title: translations.positions.title,
            key: 'Positions',
            component: <PositionsPage />,
            showTab: useFeatureFlag(DemeterFeatureType.FarmerMarginCalculator) && usePermission(DemeterPermissionType.FarmerMarginAdministrator),
            testId: 'PositionsTab',
        },
        {
            title: translations.marketIndicatorsManagement.title,
            key: 'MarketIndicators',
            component: <MarketIndicatorsMangementPage />,
            showTab:
                useFeatureFlag(DemeterFeatureType.MarketIndicators) &&
                (usePermission(DemeterPermissionType.MarketIndicatorAdministrator) ||
                    usePermission(DemeterPermissionType.MarketIndicatorDeveloper) ||
                    usePermission(DemeterPermissionType.MarketIndicatorCompliance) ||
                    usePermission(DemeterPermissionType.MarketIndicatorSubjectMatterExpert)),
            testId: 'AdministrationCommodityIndicatorManagementTab',
        },
        {
            title: translations.murexInstrumentMapping.title,
            key: 'MurexInstrumentMapping',
            component: <MurexInstrumentMappingPage />,
            showTab: usePermission(DemeterPermissionType.MurexInstrumentMappingAdministrator),
            testId: 'MurexInstrumentMappingTab',
        },
        {
            title: translations.workers.title,
            key: 'Workers',
            component: <WorkerLogsPage />,
            showTab: isAdministrator,
            testId: 'WorkersTab',
        },
        {
            title: translations.maintenance.title,
            key: 'Maintenance',
            component: <MaintenancePage />,
            showTab: isAdministrator,
            testId: 'MaintenanceTab',
        },
        {
            title: translations.analytics.title,
            key: 'Analytics',
            component: <AnalyticsPage />,
            showTab: isAdministrator,
            testId: 'AnalyticsTab',
        },
    ];

    if (isPremiumUser) {
        return <CompanyUsersPage />;
    }

    return (
        <div className={styles.master_page_container}>
            <PageHeader title={pageTitle} testId="AdministrationPageHeader" />
            <Tabs tabOptions={tabOptions} track={{ eventCategory: 'Administration', eventAction: 'Tab' }} />
        </div>
    );
};

export default AdministrationPage;
